import Button from '@components/V4/Button';
import SkeletonBox from '@components/V4/SkeletonBox';
import { useAuthState } from '@contexts/AuthContext';
import { SESSION_STATUSES } from '@shared/constants';
import { SessionStatus } from '@shared/enums';
import { convertSecondsToTime } from 'apps/agora/src/utils/helpers';
import { SessionDetailsContext } from '@modules/SessionDetails/context/SessionDetailsContext';
import moment from 'moment';
import { useContext } from 'react';
import SessionDetailsLayoutCard from '../components/SessionDetailsLayoutCard';
import CourseAbbreviationCard from './CourseAbbreviationCard';
import DetailsHeaderParticipantsList from './DetailsHeaderParticipantsList';
import HeaderLabelAndValue from './HeaderLabelAndValue';
import { DetailsHeaderProps } from './SessionDetailsHeader';
import SessionDetailsNavigationArrows from './SessionDetailsNavigationArrows';
import SessionSummaryCard from './SessionSummaryCard';
import { useTranslation } from 'apps/agora/src/TranslationContext';

const DetailsHeaderDesktop = (props: DetailsHeaderProps) => {
  const {
    abbreviation,
    title,
    sessionSummary,
    agenda,
    scheduledDate,
    endDate,
    mentor,
    student,
    accentColor,
    nextSession,
    prevSession,
    meetingDuration,
    sessionStatus,
    setIsMentorFeedbackOpen,
    onMeetingModalOpen,
    billableTime,
    billableTimeReason,
  } = props;

  const { isMentor: isMentorAuth } = useAuthState();
  const { t } = useTranslation();
  const { isLoading, isParentView } = useContext(SessionDetailsContext);
  const shouldDisplay = isMentorAuth && !isParentView;

  return (
    <SessionDetailsLayoutCard className="hidden gap-8 tablet:flex">
      <div className="flex flex-col gap-4">
        <div className="flex gap-8">
          <CourseAbbreviationCard
            abbreviation={abbreviation}
            accentColor={accentColor}
            isLoading={isLoading}
          />
          <div className="flex flex-col gap-4 w-full">
            <div className="flex justify-between">
              {isLoading ? (
                <SkeletonBox className="w-96 h-12" />
              ) : (
                <div className="flex gap-4">
                  <h1
                    className="text-3.5xl font-raleway font-semibold"
                    style={{ color: accentColor }}
                  >
                    {title}
                  </h1>

                  <div className="flex items-center gap-2">
                    <DetailsHeaderParticipantsList
                      mentor={mentor}
                      student={student}
                      accentColor={accentColor}
                    />
                  </div>
                </div>
              )}
              <SessionDetailsNavigationArrows nextSession={nextSession} prevSession={prevSession} />
            </div>

            <div className="flex flex-col gap-3">
              <HeaderLabelAndValue
                isLoading={isLoading}
                label={t('sessionDetails.endDate')}
                value={endDate ? moment(endDate).format('DD.MM.YYYY, HH:mm') : undefined}
              />

              {!endDate && !!scheduledDate && (
                <HeaderLabelAndValue
                  label={t('sessionDetails.scheduledDate')}
                  value={moment(scheduledDate).format('DD.MM.YYYY, HH:mm')}
                />
              )}

              {!isLoading && sessionStatus === SessionStatus.PENDING && shouldDisplay && (
                <Button
                  buttonText={t('sessionDetails.scheduleMeeting')}
                  className="w-36"
                  onClick={onMeetingModalOpen}
                />
              )}

              {shouldDisplay && (
                <HeaderLabelAndValue
                  isLoading={isLoading}
                  label={t('sessionDetails.sessionStatus')}
                  value={sessionStatus ? SESSION_STATUSES[sessionStatus] : undefined}
                />
              )}

              {!!meetingDuration && (
                <HeaderLabelAndValue
                  label={t('sessionDetails.meetingDuration')}
                  value={convertSecondsToTime(meetingDuration ?? 0)}
                />
              )}

              {shouldDisplay && (
                <HeaderLabelAndValue
                  isLoading={isLoading}
                  label={t('sessionDetails.billableTime')}
                  value={billableTime === 0 ? '0m' : convertSecondsToTime(billableTime)}
                />
              )}

              {shouldDisplay && !!billableTimeReason && (
                <HeaderLabelAndValue
                  isLoading={isLoading}
                  label={t('sessionDetails.billableTimeReason')}
                  value={billableTimeReason}
                />
              )}

              {!!agenda && (
                <HeaderLabelAndValue label={t('sessionDetails.agenda')} value={agenda} />
              )}
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <SkeletonBox className="h-[168px] w-full rounded-lg" />
      ) : (
        <SessionSummaryCard
          needsFeedback={sessionStatus === SessionStatus.NEEDS_FEEDBACK}
          sessionSummary={sessionSummary}
          accentColor={accentColor}
          setIsMentorFeedbackOpen={setIsMentorFeedbackOpen}
        />
      )}
    </SessionDetailsLayoutCard>
  );
};

export default DetailsHeaderDesktop;
